import { useEffect, useState } from "react";
import { authService } from "../services/auth";

const API_URL = process.env.REACT_APP_API_URL || process.env.VITE_API_URL || "http://localhost:8080";
const MCP_HEADER = { "X-MCP-Protocol": "1.0" };

const initialForm = {
  name: "",
  document: "",
  email: "",
  phone: "",
  address: "",
  city: "",
  state: "",
  zipCode: "",
  occupation: "",
  employer: "",
  monthlyIncome: 0,
};

export default function Tenants({ keycloak }) {
  const [tenants, setTenants] = useState([]);
  const [form, setForm] = useState(initialForm);
  const [loading, setLoading] = useState(false);
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [creditAnalyses, setCreditAnalyses] = useState([]);
  const [showCreditForm, setShowCreditForm] = useState(false);
  const userPlan = authService.getUser()?.plan || "FREE";
  const isFreePlan = userPlan === "FREE";
  const [creditForm, setCreditForm] = useState({
    contractId: "",
    requestedAmount: 0,
    monthlyIncome: 0,
    monthlyExpenses: 0,
    notes: "",
  });

  const headers = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${keycloak.token}`,
    ...MCP_HEADER,
  };

  const loadTenants = async () => {
    const response = await fetch(`${API_URL}/api/tenants`, { headers });
    const data = await response.json();
    setTenants(data || []);
  };

  useEffect(() => {
    loadTenants().catch((error) => console.error("Erro ao carregar locatários", error));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const createTenant = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/tenants`, {
        method: "POST",
        headers,
        body: JSON.stringify(form),
      });
      const created = await response.json();
      setTenants((prev) => [created, ...prev]);
      setForm(initialForm);
    } catch (error) {
      console.error("Erro ao criar locatário", error);
    } finally {
      setLoading(false);
    }
  };

  const loadCreditAnalyses = async (tenantId) => {
    const response = await fetch(`${API_URL}/api/credit/tenant/${tenantId}`, { headers });
    const data = await response.json();
    setCreditAnalyses(data || []);
    setSelectedTenant(tenantId);
    setShowCreditForm(true);
  };

  const analyzeCredit = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/credit/analyze/${selectedTenant}`, {
        method: "POST",
        headers,
        body: JSON.stringify(creditForm),
      });
      const analysis = await response.json();
      setCreditAnalyses((prev) => [analysis, ...prev]);
      setCreditForm({
        contractId: "",
        requestedAmount: 0,
        monthlyIncome: 0,
        monthlyExpenses: 0,
        notes: "",
      });
      setShowCreditForm(false);
    } catch (error) {
      console.error("Erro ao analisar crédito", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container">
      <section className="hero">
        <h1>Cadastro de Locatários</h1>
        <p>Gerencie os inquilinos cadastrados no sistema.</p>
      </section>

      <section className="panel">
        <h2 className="section-title">Novo Locatário</h2>
        <div className="grid grid-3">
          <input
            placeholder="Nome completo"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
          />
          <input
            placeholder="CPF/CNPJ"
            value={form.document}
            onChange={(e) => setForm({ ...form, document: e.target.value })}
          />
          <input
            type="email"
            placeholder="E-mail"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
          />
          <input
            placeholder="Telefone"
            value={form.phone}
            onChange={(e) => setForm({ ...form, phone: e.target.value })}
          />
          <input
            placeholder="Endereço"
            value={form.address}
            onChange={(e) => setForm({ ...form, address: e.target.value })}
          />
          <input
            placeholder="Cidade"
            value={form.city}
            onChange={(e) => setForm({ ...form, city: e.target.value })}
          />
          <input
            placeholder="Estado"
            value={form.state}
            onChange={(e) => setForm({ ...form, state: e.target.value })}
          />
          <input
            placeholder="CEP"
            value={form.zipCode}
            onChange={(e) => setForm({ ...form, zipCode: e.target.value })}
          />
          <input
            placeholder="Profissão"
            value={form.occupation}
            onChange={(e) => setForm({ ...form, occupation: e.target.value })}
          />
          <input
            placeholder="Empregador"
            value={form.employer}
            onChange={(e) => setForm({ ...form, employer: e.target.value })}
          />
          <input
            type="number"
            placeholder="Renda mensal"
            value={form.monthlyIncome}
            onChange={(e) => setForm({ ...form, monthlyIncome: Number(e.target.value) })}
          />
        </div>
        <button className="btn-primary" type="button" onClick={createTenant} disabled={loading}>
          {loading ? "Salvando..." : "Cadastrar Locatário"}
        </button>
      </section>

      <section>
        <h2 className="section-title">Locatários Cadastrados</h2>
        <div className="grid grid-3">
          {tenants.map((tenant) => (
            <div key={tenant.id} className="listing-card">
              <h4>{tenant.name}</h4>
              <div>Documento: {tenant.document}</div>
              <div>E-mail: {tenant.email}</div>
              <div>Telefone: {tenant.phone}</div>
              <div>{tenant.city}, {tenant.state}</div>
              {tenant.occupation && <div>Profissão: {tenant.occupation}</div>}
              {tenant.monthlyIncome > 0 && <div>Renda: R$ {tenant.monthlyIncome.toLocaleString("pt-BR")}</div>}
              {isFreePlan ? (
                <div style={{ 
                  marginTop: "1rem", 
                  padding: "0.75rem", 
                  background: "var(--border-light)", 
                  borderRadius: "var(--radius-md)",
                  fontSize: "0.85rem",
                  color: "var(--text-secondary)",
                  textAlign: "center"
                }}>
                  🔒 Análise de Crédito disponível apenas em planos pagos
                </div>
              ) : (
                <button
                  className="btn-secondary"
                  onClick={() => loadCreditAnalyses(tenant.id)}
                  style={{ marginTop: "1rem" }}
                >
                  🔍 Análise de Crédito
                </button>
              )}
            </div>
          ))}
        </div>
      </section>

      {showCreditForm && (
        <section className="panel" style={{ marginTop: "2rem" }}>
          <h2 className="section-title">Análise de Crédito</h2>
          <div className="grid grid-3">
            <input
              placeholder="ID do Contrato (opcional)"
              value={creditForm.contractId}
              onChange={(e) => setCreditForm({ ...creditForm, contractId: e.target.value })}
            />
            <input
              type="number"
              placeholder="Valor solicitado"
              value={creditForm.requestedAmount}
              onChange={(e) => setCreditForm({ ...creditForm, requestedAmount: Number(e.target.value) })}
            />
            <input
              type="number"
              placeholder="Renda mensal"
              value={creditForm.monthlyIncome}
              onChange={(e) => setCreditForm({ ...creditForm, monthlyIncome: Number(e.target.value) })}
            />
            <input
              type="number"
              placeholder="Despesas mensais"
              value={creditForm.monthlyExpenses}
              onChange={(e) => setCreditForm({ ...creditForm, monthlyExpenses: Number(e.target.value) })}
            />
            <textarea
              placeholder="Observações"
              value={creditForm.notes}
              onChange={(e) => setCreditForm({ ...creditForm, notes: e.target.value })}
              style={{ gridColumn: "1 / -1" }}
            />
          </div>
          <div style={{ display: "flex", gap: "1rem", marginTop: "1rem" }}>
            <button className="btn-primary" onClick={analyzeCredit} disabled={loading}>
              {loading ? "Analisando..." : "Analisar Crédito"}
            </button>
            <button className="btn-secondary" onClick={() => setShowCreditForm(false)}>
              Cancelar
            </button>
          </div>
        </section>
      )}

      {creditAnalyses.length > 0 && (
        <section style={{ marginTop: "2rem" }}>
          <h2 className="section-title">Histórico de Análises de Crédito</h2>
          <div className="grid grid-3">
            {creditAnalyses.map((analysis) => (
              <div key={analysis.id} className="listing-card">
                <div
                  className="tag"
                  style={{
                    background:
                      analysis.status === "APROVADO"
                        ? "var(--brand-green)20"
                        : analysis.status === "REPROVADO"
                        ? "#dc262620"
                        : "#f59e0b20",
                    color:
                      analysis.status === "APROVADO"
                        ? "var(--brand-green)"
                        : analysis.status === "REPROVADO"
                        ? "#dc2626"
                        : "#f59e0b",
                  }}
                >
                  {analysis.status}
                </div>
                <div
                  style={{
                    padding: "0.25rem 0.75rem",
                    borderRadius: "999px",
                    fontSize: "0.75rem",
                    fontWeight: 600,
                    display: "inline-block",
                    marginTop: "0.5rem",
                    background:
                      analysis.riskLevel === "BAIXO"
                        ? "var(--brand-green)20"
                        : analysis.riskLevel === "MEDIO"
                        ? "#f59e0b20"
                        : "#dc262620",
                    color:
                      analysis.riskLevel === "BAIXO"
                        ? "var(--brand-green)"
                        : analysis.riskLevel === "MEDIO"
                        ? "#f59e0b"
                        : "#dc2626",
                  }}
                >
                  Risco: {analysis.riskLevel}
                </div>
                <div style={{ fontSize: "1.5rem", fontWeight: 700, margin: "0.75rem 0", color: "var(--brand-blue)" }}>
                  Score: {analysis.creditScore}
                </div>
                <div style={{ color: "var(--text-secondary)" }}>
                  Valor solicitado: R$ {analysis.requestedAmount?.toLocaleString("pt-BR", { minimumFractionDigits: 2 })}
                </div>
                <div style={{ color: "var(--text-secondary)" }}>
                  Renda: R$ {analysis.monthlyIncome?.toLocaleString("pt-BR", { minimumFractionDigits: 2 })}
                </div>
                {analysis.recommendation && (
                  <div style={{ marginTop: "0.75rem", padding: "0.75rem", background: "#f5f5f5", borderRadius: "var(--radius-md)" }}>
                    <strong>Recomendação:</strong> {analysis.recommendation}
                  </div>
                )}
                {analysis.checks && analysis.checks.length > 0 && (
                  <div style={{ marginTop: "0.75rem" }}>
                    <strong>Consultas realizadas:</strong>
                    {analysis.checks.map((check, idx) => (
                      <div key={idx} style={{ fontSize: "0.9rem", color: "var(--text-secondary)", marginTop: "0.25rem" }}>
                        {check.source}: {check.status} (Score: {check.score})
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
